import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { updateReportSubscription } from '../../utils/api';
import { ScheduleBlock, MAX_TIMES } from '../ReportsNative/schedule/ScheduleBlocks';
import { PERIODS, timezoneChoices } from '../ReportsNative/schedule/periods';
import '../ReportsNative/ReportsNative.css';

/**
 * Compact editor for nativePages report subscriptions on the Subscriptions hub.
 * Edits delivery details (subject, schedule, period, CC) — the report scope
 * (filters/columns) stays as saved from the report page that created it.
 */

const fieldStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#111', boxSizing: 'border-box', background: '#fff' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 };
// "Use this view's date range" needs the report page's view; the hub has none.
const HUB_PERIODS = PERIODS.filter((p) => p.value !== 'currentView');

const NativePagesScheduleEditModal = ({ subscription, onSaved, onCancel }) => {
  const trigger = subscription.triggerTime || {};
  const emailConfig = subscription.deliveryConfig?.emailConfig || {};

  const timezone = trigger.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [name, setName] = useState(subscription.name || emailConfig.customSubject || '');
  // Same control as the in-report drawer, and the same state shape: editing a
  // subject here must not quietly collapse a schedule that fires twice a day,
  // on two days of the month, or on a close-date anchor.
  const [schedule, setSchedule] = useState({
    frequency: trigger.frequency || 'daily',
    days: Array.isArray(trigger.days) && trigger.days.length
      ? trigger.days
      : (trigger.frequency === 'monthly' ? [1] : [0, 1, 2, 3, 4, 5, 6]),
    times: Array.isArray(trigger.times) && trigger.times.length ? trigger.times.slice(0, MAX_TIMES) : ['08:00'],
    timezone,
    ...(trigger.anchor ? { anchor: trigger.anchor } : {}),
  });
  const [period, setPeriod] = useState(subscription.period || 'mtd');
  const [ccInput, setCcInput] = useState((emailConfig.ccRecipients || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const cc = ccInput.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => /.+@.+\..+/.test(s));
    // An anchored monthly schedule derives its day per month and carries no
    // day list; everything else keeps the one the block maintains.
    const triggerTime = schedule.anchor
      ? { frequency: schedule.frequency, times: schedule.times, timezone: schedule.timezone, anchor: schedule.anchor }
      : { frequency: schedule.frequency, days: schedule.days, times: schedule.times, timezone: schedule.timezone };
    const subject = name || 'Lodgeic Report';
    try {
      const updated = await updateReportSubscription(subscription.id, {
        userId: subscription.userId,
        name: subject,
        period,
        triggerTime,
        deliveryConfig: {
          ...(subscription.deliveryConfig || {}),
          deliveryMethod: 'email',
          emailConfig: { ...emailConfig, ccRecipients: cc, customSubject: subject },
        },
      });
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setSaving(false);
    }
  };

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: 17, fontWeight: 600, color: '#111', marginBottom: 6 }}>Edit scheduled report email</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          The report scope — filters, columns, snapshot window and row depth — stays as saved. Edit it from the report page this schedule was created on.
        </p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Email subject</label>
          <input style={fieldStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="Lodgeic report" />
        </div>

        <ScheduleBlock
          trigger={schedule}
          onTrigger={setSchedule}
          timezones={timezoneChoices(timezone)}
        />

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Data period</label>
          <select style={fieldStyle} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {HUB_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            {!HUB_PERIODS.some((p) => p.value === period) && <option value={period}>{period}</option>}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>CC recipients (optional)</label>
          <input style={fieldStyle} value={ccInput} onChange={(e) => setCcInput(e.target.value)} placeholder="gm@hotel.com, owner@group.com" />
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Separate addresses with commas. Recipients receive the same permission-scoped PDF.</p>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{ flex: 1, padding: '9px 0', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 14, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ flex: 1, padding: '9px 0', border: 'none', borderRadius: 8, background: '#f97316', color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NativePagesScheduleEditModal;
